import React, { useCallback, useState, useMemo } from "react";
import { TableRowActionProps } from "./index";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import SweetAlert from "sweetalert2";
import { useIntl, FormattedMessage, defineMessages } from "react-intl";
import { SortableHandle } from "react-sortable-hoc";
import { useCrudNavigation } from "../crud/container";
import { Identifiable } from "../context";
import { Message } from "../i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { faSync, faPencilAlt, faEye, faCopy } from "@fortawesome/free-solid-svg-icons";
import SwapArrow from "../../witadmin/icons/swapArrow";
import styled from "styled-components";
import ReactTooltip from "react-tooltip";

import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { APP_COLORS } from "../../witadmin/Common/constants";
import { v4 as uuidv4 } from "uuid";

// Translations
const tooltipActionMessages = defineMessages({
  delete: {
    id: "app.entityList.tooltip.delete",
    description: "Delete message",
    defaultMessage: "Delete",
  },
  drag: {
    id: "app.entityList.tooltip.reorder",
    description: "Reorder message",
    defaultMessage: "Reorder",
  },
  edit: {
    id: "app.entityList.tooltip.edit",
    description: "Edit message",
    defaultMessage: "Edit",
  },
  view: {
    id: "app.entityList.tooltip.view",
    description: "View message",
    defaultMessage: "View",
  },
  duplicate: {
    id: "app.entityList.tooltip.duplicate",
    description: "Duplicate message",
    defaultMessage: "Duplicate",
  },
});

// Styled components
const StyledSwapArrow = styled(SwapArrow)`
  height: 18px;
  width: 18px;
`;
export const StyledReactTooltip = styled(ReactTooltip)`
  text-transform: none;
`;

export interface EditProps<T extends Identifiable> {
  onGoToEdit(id: string): void;
  readonly?: boolean;
  row: T;
}

export interface DuplicateProps<T extends Identifiable> {
  onGoToDuplicate(id: string): void;
  row: T;
}

export function Edit<T extends Identifiable>({ row, onGoToEdit, readonly = false }: EditProps<T>) {
  const execute = useCallback(() => {
    //We store the preloaded row on session storage so it can be recovered in the edit window.
    window.sessionStorage.setItem(`preloaded_${row.id}`, JSON.stringify(row));
    onGoToEdit(row.id.toString());
  }, [row, onGoToEdit]);

  return (
    <Button
      data-tip="React-tooltip"
      data-for={readonly ? "view" : "edit"}
      className="mr-2"
      size="sm"
      variant="light"
      onClick={execute}>
      <FontAwesomeIcon icon={readonly ? faEye : faPencilAlt} />
    </Button>
  );
}

export function NestedEdit({
  openNestedFormModal,
  id,
}: {
  openNestedFormModal?: (id?: string) => void;
  id: any;
}) {
  return (
    <>
      <Button
        data-tip="React-tooltip"
        data-for="edit"
        className="mr-2"
        size="sm"
        variant="light"
        onClick={() => openNestedFormModal && openNestedFormModal(id)}>
        <FontAwesomeIcon icon={faPencilAlt} />
      </Button>
    </>
  );
}

const ReorderHandle = SortableHandle(() => (
  <Button
    // data-tip="React-tooltip"
    // data-for="drag"
    style={{ cursor: "move" }}
    variant="light"
    className="btn btn-default btn-sm mr-2">
    <StyledSwapArrow />
  </Button>
));

/**
 * Reorder is managed directly by the list component, but the action must
 * be rendered so the drag handle is displayed.
 * @param param0
 */
export function Reorder() {
  return <ReorderHandle />;
}

/**
 * Answer of `beforeDelete`: `confirmed` when it already asked the user (the generic confirmation is
 * skipped), `proceed` to show the generic confirmation as usual, `abort` to do nothing.
 */
export type BeforeDeleteDecision = "proceed" | "confirmed" | "abort";

export interface DeleteProps<T extends Identifiable> extends TableRowActionProps<T> {
  onDelete(id: T["id"]): Promise<void>;

  /** Runs before the generic confirmation; when it fails, nothing is deleted. */
  beforeDelete?(row: T): Promise<BeforeDeleteDecision>;

  shouldDisable?(value: T): string | false;

  messages?: DeleteActionMessages;
  useOverlay?: boolean;
}

export interface DeleteActionMessages {
  deleteTooltipMessage: Message;
  deleteDisabled: Message;
  deleteDisabledDescription: Message;
  deleteConfirmation: Message;
  deleteConfirmationWarning: Message;
  deleteOk: Message;
  deleteCancel: Message;
  deleteFailed: Message;
}

export const defaultDeleteMessages: DeleteActionMessages = defineMessages({
  deleteTooltipMessage: {
    id: "delete",
    description: "Delete message",
    defaultMessage: "Delete",
  },
  deleteDisabled: {
    id: "deleteDisabled",
    defaultMessage: "This item cannot be deleted",
  },
  deleteDisabledDescription: {
    id: "deleteDisabledDescription",
    defaultMessage: "This item is in use and cannot be deleted.",
  },
  deleteConfirmation: {
    id: "default.deleteConfirmation",
    defaultMessage: "Are you sure?",
  },
  deleteConfirmationWarning: {
    id: "default.deleteConfirmationWarning",
    defaultMessage: "Once deleted, you will not be able to recover this entity!",
  },
  deleteCancel: {
    id: "default.cancel",
    defaultMessage: "Cancel",
  },
  deleteOk: {
    id: "default.ok",
    defaultMessage: "OK",
  },
  deleteFailed: {
    id: "default.failed",
    defaultMessage: "Failed",
  },
});

export function NestedDelete({
  deleteChildForm,
  id,
}: {
  deleteChildForm?: (id: string) => void;
  id: any;
}) {
  const handleNestedDelete = useCallback(() => {
    deleteChildForm && deleteChildForm(id);
  }, [deleteChildForm, id]);

  return (
    <Button
      size="sm"
      variant="danger"
      onClick={handleNestedDelete}
      data-tip="React-tooltip"
      data-for="delete">
      <FontAwesomeIcon icon={faTrashAlt} />
    </Button>
  );
}

export function Delete<T extends Identifiable>({
  row,
  onDelete,
  onReloadList,
  shouldDisable,
  beforeDelete,
  messages = defaultDeleteMessages,
  useOverlay = false,
}: DeleteProps<T>) {
  const intl = useIntl();
  const [enabled, setEnabled] = useState(true);
  const userDisabled = useMemo(() => shouldDisable && shouldDisable(row), [shouldDisable, row]);
  const execute = useCallback(async () => {
    if (userDisabled) {
      await SweetAlert.fire({
        title: intl.formatMessage(messages.deleteDisabled),
        icon: "warning",
        text: intl.formatMessage(messages.deleteDisabledDescription),
      });
    } else {
      let decision: BeforeDeleteDecision = "proceed";
      if (beforeDelete) {
        try {
          setEnabled(false);
          decision = await beforeDelete(row);
        } catch (e) {
          console.log(e);
          await SweetAlert.fire({
            title: intl.formatMessage(messages.deleteFailed),
            icon: "error",
          });
          return;
        } finally {
          setEnabled(true);
        }
      }
      if (decision === "abort") {
        return;
      }
      if (decision === "confirmed") {
        try {
          setEnabled(false);
          await onDelete(row.id);
        } catch (e) {
          console.log(e);
          await SweetAlert.fire({
            title: intl.formatMessage(messages.deleteFailed),
            icon: "error",
          });
          return;
        } finally {
          setEnabled(true);
        }
        await onReloadList();
        return;
      }
      await SweetAlert.fire({
        title: intl.formatMessage(messages.deleteConfirmation),
        text: intl.formatMessage(messages.deleteConfirmationWarning),
        icon: "warning",
        customClass: {
          confirmButton: "btn-danger",
        },
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: intl.formatMessage(messages.deleteOk),
        cancelButtonText: intl.formatMessage(messages.deleteCancel),
        confirmButtonColor: APP_COLORS.green,
        preConfirm: async () => {
          let success: boolean;
          try {
            setEnabled(false);
            await onDelete(row.id);
            success = true;
          } catch (e) {
            SweetAlert.showValidationMessage(intl.formatMessage(messages.deleteFailed));
            console.log(e);
            success = false;
          } finally {
            setEnabled(true);
          }
          if (success) await onReloadList();
        },
      });
    }
  }, [intl, row, onDelete, onReloadList, userDisabled, messages, beforeDelete]);

  return useOverlay ? (
    <OverlayTrigger
      delay={{ show: 250, hide: 0 }}
      overlay={props => (
        <Tooltip id={`delete-tooltip-${row.id}`} {...props}>
          <FormattedMessage {...defaultDeleteMessages.deleteTooltipMessage} />
        </Tooltip>
      )}>
      <Button
        disabled={!enabled}
        size="sm"
        className={`text-white ${userDisabled ? "disabled" : ""}`}
        variant="danger"
        onClick={execute}>
        <FontAwesomeIcon icon={faTrashAlt} />
      </Button>
    </OverlayTrigger>
  ) : (
    <Button
      disabled={!enabled}
      size="sm"
      className={`text-white ${userDisabled ? "disabled" : ""}`}
      variant="danger"
      onClick={execute}
      data-tip="React-tooltip"
      data-for="delete">
      <FontAwesomeIcon icon={faTrashAlt} />
    </Button>
  );
}

export interface CreateActionMessages {
  create: Message;
}

export const defaultCreateActionMessages: CreateActionMessages = defineMessages({
  create: {
    id: "default.create",
    defaultMessage: "Create",
  },
});

interface CreateProps {
  onGoToCreate(): void;

  messages?: CreateActionMessages;
}

interface NestedCreateProps {
  openNestedFormModal?: (id?: string) => void;
  messages?: CreateActionMessages;
}

export function Create({ onGoToCreate, messages = defaultCreateActionMessages }: CreateProps) {
  return (
    <Button className="mr-2" onClick={onGoToCreate}>
      <FormattedMessage {...messages.create} />
    </Button>
  );
}

export function NestedCreate({
  openNestedFormModal,
  messages = defaultCreateActionMessages,
}: NestedCreateProps) {
  return (
    <>
      <Button className="mr-2" onClick={() => openNestedFormModal && openNestedFormModal()}>
        <FormattedMessage {...messages.create} />
      </Button>
    </>
  );
}

interface RefreshProps {
  onReloadList(): void;
}

export function Refresh({ onReloadList }: RefreshProps) {
  return (
    <Button onClick={onReloadList}>
      <FontAwesomeIcon icon={faSync} />
    </Button>
  );
}

export interface DefaultTableRowActionsProps<T extends Identifiable>
  extends TableRowActionProps<T> {
  showReorder?: boolean;
  showEdit?: boolean;
  showNestedActions?: boolean;
  messages?: DeleteActionMessages;
  onDelete?(id: string): Promise<void>;
  deleteChildForm?: (id: string) => void;
  openNestedFormModal?: (id?: string) => void;
  disableDelete?(value: T): string | false;
}

export const defaultTableActionsMessages = {
  ...defaultDeleteMessages,
};

export function DefaultTableRowActions<T extends Identifiable>({
  onReloadList,
  row,
  rest,
  onDelete,
  showEdit,
  showReorder,
  disableDelete,
  messages = defaultTableActionsMessages,
  showNestedActions,
  openNestedFormModal,
  deleteChildForm,
}: DefaultTableRowActionsProps<T>) {
  const navigation = useCrudNavigation();
  return (
    <Table.Cell {...rest}>
      {showReorder && <Reorder />}
      {showEdit && <Edit onGoToEdit={navigation.goToEditForm} row={row} />}
      {showNestedActions && openNestedFormModal && (
        <NestedEdit openNestedFormModal={openNestedFormModal} id={row?.id} />
      )}
      {onDelete && (
        <Delete
          rest={rest}
          messages={messages}
          onDelete={onDelete}
          onReloadList={onReloadList}
          row={row}
          shouldDisable={disableDelete}
        />
      )}
      {showNestedActions && deleteChildForm && (
        <NestedDelete deleteChildForm={deleteChildForm} id={row?.id} />
      )}
    </Table.Cell>
  );
}

export interface DefaultTableActionsProps {
  onReloadList(): void;
  showCreate?: boolean;
  showRefresh?: boolean;
  showNestedCreate?: boolean;
  openNestedFormModal?: (id?: string) => void;
}

export function DefaultTableActions({
  onReloadList,
  showCreate,
  showRefresh,
  showNestedCreate,
  openNestedFormModal,
}: DefaultTableActionsProps) {
  const navigation = useCrudNavigation();

  return (
    <>
      {showCreate && <Create onGoToCreate={navigation.goToCreate} />}
      {showNestedCreate && <NestedCreate openNestedFormModal={openNestedFormModal} />}
      {showRefresh && <Refresh onReloadList={onReloadList} />}
    </>
  );
}

export function DefaultTableRowActionsTooltip({
  showDelete,
  showEdit,
  showReorder,
  showDuplicate,
}: {
  showDelete: boolean;
  showEdit: boolean;
  showReorder: boolean;
  showDuplicate: boolean;
}) {
  return useMemo(
    () => (
      <>
        {showDelete && (
          <StyledReactTooltip id="delete" effect="solid">
            <FormattedMessage {...tooltipActionMessages.delete} />
          </StyledReactTooltip>
        )}
        {showEdit && (
          <>
            <StyledReactTooltip id="edit" effect="solid">
              <FormattedMessage {...tooltipActionMessages.edit} />
            </StyledReactTooltip>
            <StyledReactTooltip id="view" effect="solid">
              <FormattedMessage {...tooltipActionMessages.view} />
            </StyledReactTooltip>
          </>
        )}
        {showReorder && (
          <StyledReactTooltip id="drag" effect="solid">
            <FormattedMessage {...tooltipActionMessages.drag} />
          </StyledReactTooltip>
        )}
        {showDuplicate && (
          <StyledReactTooltip id="duplicate" effect="solid">
            <FormattedMessage {...tooltipActionMessages.duplicate} />
          </StyledReactTooltip>
        )}
      </>
    ),
    [showDelete, showDuplicate, showEdit, showReorder]
  );
}

export function Duplicate<T extends Identifiable>({ row, onGoToDuplicate }: DuplicateProps<T>) {
  const execute = useCallback(() => {
    const newID = uuidv4();
    //We store the preloaded row on session storage so it can be recovered in the edit window.
    window.sessionStorage.setItem(`duplicate_preloaded_${newID}`, JSON.stringify(row));
    onGoToDuplicate(newID.toString());
  }, [row, onGoToDuplicate]);

  return (
    <Button
      data-tip="React-tooltip"
      data-for={"duplicate"}
      className="mr-2"
      size="sm"
      variant="light"
      onClick={execute}>
      <FontAwesomeIcon icon={faCopy} />
    </Button>
  );
}
